// @flow

import url from 'url';
import * as React from 'react';
import type { FormValues } from 'gandi.react-form';
import type { FormProps } from 'redux-form';

import {
  Base,
  Disclosure,
  DisclosureContent,
  DisclosureTrigger,
  Cell,
  IconButton,
  OrderedList,
  TypoListHelp as ListHelp,
  TypoListItem,
  TypoListTitle as ListTitle,
  UnorderedList,
} from '@gandi/ui';

import {
  Action,
  ListCell,
  ListIcon,
  ListItem,
  ListRow,
} from '@gandi/ui/deprecated';
import type { TranslatorProps } from '@gandi/translate';
import type { NotifyAction } from '@gandi/notifications';
import { actions as formActions, SubmissionError } from 'gandi.react-form';
import { Intro } from 'gandi.react-public-layout';

import type { WithRouterProps } from 'src/helpers/withRouter';
import type { Dispatch, GandiLogger } from 'src/types';

import type { Subject } from '../../Support/Contact/types';
import Layout from '../../containers/Layout';
import AbuseForm from './AbuseForm';
import { sendSuccess } from '../modules';

import GandiVector from '../../GandiVector';

import styles from './Abuse.scss';

export type OwnProps = {|
  initialValues: {| [key: string]: string | boolean |},
  publicUrlV5: string,
  success: boolean,
  updating: boolean,
|};

type DispatchProps = {|
  notifyDanger: NotifyAction,
  // Use in prefetch
  // eslint-disable-next-line react/no-unused-prop-types
  sendSuccess: () => void,
  submitAbuseForm: (
    value: { ... },
    routeParams: {| subject?: Subject, category: string, topic: string |},
    __: TranslatorProps['__']
  ) => Promise<any>,
|};

export type InjectedProps = {|
  logger: GandiLogger,
  reduxFormProps: FormProps,
  translator: TranslatorProps,
  ...WithRouterProps<{|
    category?: string,
    topic?: string,
    subject?: Subject,
  |}>,
|};

export type Props = {|
  ...InjectedProps,
  ...DispatchProps,
  ...OwnProps,
|};
export default class Abuse extends React.Component<Props> {
  static preFetch(
    args: Props & {
      dispatch: Dispatch,
      req: express$Request,
      ...
    }
  ): Promise<$TupleMap<Array<any>, <T>(p: Promise<T> | T) => T>> {
    const { dispatch, req } = args;

    const promises = [];

    const query = url.parse(req.url, true).query || {};
    if ('success' in query) {
      promises.push(dispatch(sendSuccess()));

      return Promise.all(promises);
    }

    if (req && req.body) {
      promises.push(dispatch(formActions.initialize('AbuseForm', req.body)));
    }
    return Promise.all(promises);
  }

  onSubmit: (values: any) => Promise<void> | Promise<mixed> = (
    values: FormValues
  ) => {
    const {
      translator: { __ },
      logger,
      updating,
    } = this.props;
    if (
      !updating &&
      this.props.routeParams.category &&
      this.props.routeParams.topic
    ) {
      const routeParams = {
        category: this.props.routeParams.category,
        topic: this.props.routeParams.topic,
      };
      return this.props.submitAbuseForm(values, routeParams, __).catch(err => {
        if (err instanceof SubmissionError) {
          throw err;
        } else {
          this.props.notifyDanger(
            __('A temporary error occured while sending your message.')
          );
          logger.error(
            err,
            'A temporary error occured while sending your message.'
          );
        }
      });
    }

    return Promise.resolve();
  };

  renderSuccess(): React.Element<typeof React.Fragment> {
    const { __ } = this.props.translator;
    const { publicUrlV5 } = this.props;
    return (
      <>
        <div>
          <Intro
            title={__('Thank you for your report')}
            className={styles.title}
          />
          <Base marginBottom="base" className={styles.containerSuccess}>
            <ListRow className={styles.icon}>
              <ListIcon fill="success" name="success" />
              <ListCell>{__('We have received your complaint.')}</ListCell>
            </ListRow>
            <Base
              paddingRight="small"
              paddingLeft="small"
              paddingBottom="small"
            >
              <Base
                tagName="p"
                marginTop="none"
                dangerouslySetInnerHTML={__(
                  'A confirmation email has been sent to you with a ticket number that will' +
                    ' serve as <strong>the reference number of your complaint.</strong>' +
                    ' Please include this in any future communications on this matter.',
                  { withHTML: true }
                )}
              />
              <Base tagName="p" marginBottom="none">
                {__(
                  'All complaints will be handled by one of our agents 24/7.'
                )}
              </Base>
            </Base>
          </Base>
        </div>
        <ListRow
          flexAlignItemsCenter
          paddingLeft="small"
          backgroundColor="grey-lighter"
        >
          <ListTitle marginTop="small">{__('What next?')}</ListTitle>
        </ListRow>
        <Base
          padding="xsmall"
          marginBottom="base"
          backgroundColor="grey-lighter"
          itemProp="text"
        >
          <UnorderedList gap="xxsmall" icon="arrowRight" iconTheme="green">
            <TypoListItem marginLeft="small" marginRight="small">
              <span
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={__(
                  'Once your complaint has been received, ' +
                    '<strong>our dedicated team will review the details of your case</strong>' +
                    ' in light of the information that is accessible to us in our database.',
                  { withHTML: true }
                )}
              />
            </TypoListItem>
            <TypoListItem margin="small">
              <span
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={__(
                  'If you want to send us additional documents, you must write to us by email at ' +
                    '<strong>abuse@gandi.net</strong>',
                  { withHTML: true }
                )}
              />
            </TypoListItem>
            <TypoListItem margin="small">
              <span
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={__(
                  'We proceed on a case-by-case basis, and perform the necessary actions that we ' +
                    'deem necessary <strong>within the framework of current applicable law.</strong>',
                  { withHTML: true }
                )}
              />
            </TypoListItem>
          </UnorderedList>
        </Base>
        <ListRow>
          <ListItem>
            <Cell>{__('Continue to the Gandi website')}</Cell>
          </ListItem>
          <Action icon="arrowRight" href={publicUrlV5}>
            {__('Continue')}
          </Action>
        </ListRow>
      </>
    );
  }

  render(): React.Element<typeof React.Fragment> {
    const {
      location,
      reduxFormProps,
      routeParams,
      success,
      translator: { __ },
    } = this.props;

    if (success) {
      return (
        <>
          <div className={styles.banner} />
          <GandiVector />
          <Layout location={location}>{this.renderSuccess()}</Layout>
        </>
      );
    }

    return (
      <>
        <div className={styles.banner} />
        <GandiVector />
        <Layout location={location}>
          <div>
            <Intro title={__('Report abuse')} className={styles.title} />
            <Base marginBottom="base" className={styles.container}>
              <Base tagName="h2">
                {__(
                  'To file an abuse complaint for a service provided by Gandi, you can:'
                )}
              </Base>
              <OrderedList>
                <TypoListItem>
                  <ListTitle>{__('Fill out the form below')}</ListTitle>
                  {/* $FlowFixMe[prop-missing] dangerouslySetInnerHTML is missing from component's props */}
                  <ListHelp
                    dangerouslySetInnerHTML={__(
                      'The form will send your complaint to <strong>abuse@gandi.net</strong>',
                      { withHTML: true }
                    )}
                  />
                </TypoListItem>
                <TypoListItem>
                  <ListTitle>{__('Call +33.170377661')}</ListTitle>
                  <ListHelp>
                    {__(
                      'The answering machine will provide you with the procedure to follow'
                    )}
                  </ListHelp>
                </TypoListItem>
                <TypoListItem>
                  {/* $FlowFixMe[prop-missing] dangerouslySetInnerHTML is missing from component's props */}
                  <ListTitle
                    dangerouslySetInnerHTML={__(
                      'Write directly to <strong>abuse@gandi.net</strong>',
                      { withHTML: true }
                    )}
                  />
                  <ListHelp>
                    {__(
                      'Please be sure to include any elements that will back up your complaint.'
                    )}
                  </ListHelp>
                </TypoListItem>
              </OrderedList>
              <p>
                {__(
                  'All abuse complaints will be handled by one of our agents within 24h, 7 days/7.'
                )}
              </p>
            </Base>
          </div>
          <Base id="AbuseForm">
            <Intro title={__('Contact the abuse department')} />
            <AbuseForm
              onSubmit={this.onSubmit}
              reduxFormProps={reduxFormProps}
              routeParams={routeParams}
            />
          </Base>
          <Base marginBottom="base">
            <Disclosure>
              <ListRow separatorBottom>
                <ListItem
                  flexAlignItemsCenter
                  paddingLeft="small"
                  paddingRight="xsmall"
                >
                  <ListTitle
                    weight="semibold"
                    flex
                    flexJustifyBetween
                    flexAlignItemsCenter
                  >
                    {__('Learn more about the complaint procedure:')}
                    <DisclosureTrigger
                      as={IconButton}
                      icon="miniChevronBottom"
                      kind="ghost"
                      small
                    >
                      {__('Open')}
                    </DisclosureTrigger>
                  </ListTitle>
                </ListItem>
              </ListRow>
              <DisclosureContent>
                <Base
                  padding="xsmall"
                  backgroundColor="grey-lighter"
                  separatorTop
                  separatorBottom
                >
                  <UnorderedList
                    gap="xxsmall"
                    icon="arrowRight"
                    iconTheme="green"
                  >
                    <TypoListItem>
                      <span
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={__(
                          'All communication is done exclusively via email. ' +
                            'Be sure to <strong>provide us with a valid email address</strong> so that we can reply to you.',
                          { withHTML: true }
                        )}
                      />
                    </TypoListItem>
                    <TypoListItem>
                      <span
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={__(
                          'For every new complaint you send, you will automatically receive a confirmation' +
                            ' email containing a case ticket reference number in the subject. ' +
                            'To assure the most efficient handling of your case, please be sure to ' +
                            '<strong>include this number in all your correspondences with us regarding your complaint.</strong>',
                          { withHTML: true }
                        )}
                      />
                    </TypoListItem>
                    <TypoListItem>
                      <span
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={__(
                          'Once your complaint has been received, ' +
                            '<strong>our dedicated team will review the details</strong> of your case in light of' +
                            ' the information that is accessible to us in our database.',
                          { withHTML: true }
                        )}
                      />
                    </TypoListItem>
                    <TypoListItem>
                      <span
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={__(
                          'We proceed on a case-by-case basis, and perform the necessary actions that ' +
                            'we deem necessary <strong>within the framework of current applicable law.</strong>',
                          { withHTML: true }
                        )}
                      />
                    </TypoListItem>
                  </UnorderedList>
                </Base>
              </DisclosureContent>
            </Disclosure>
          </Base>
        </Layout>
      </>
    );
  }
}
